import React, { Suspense, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import ProtectedRoute from './ProtectedRoute';
import { METRIKA_GOALS } from '../../shared/config/metrics';
import { trackGoal, trackPageView } from '../../shared/lib/metrika';
import publicSite from '../../shared/config/publicSite';
import { useI18n } from '../../shared/config/i18n';
import { clearPublicSeo } from '../../shared/lib/documentSeo';
import MeasurementTouchTracker from './MeasurementTouchTracker';

// Ленивая загрузка страниц
const LoginPage = React.lazy(() => import('../../pages/Login'));
const DiaryPage = React.lazy(() => import('../../pages/Diary'));
const ChatPage = React.lazy(() => import('../../pages/Chat'));
const CalendarPage = React.lazy(() => import('../../pages/Calendar'));
const TrackerPage = React.lazy(() => import('../../pages/Tracker'));
const AnalyticsPage = React.lazy(() => import('../../pages/Analytics'));
const BrainPage = React.lazy(() => import('../../pages/Brain'));
const SettingsPage = React.lazy(() => import('../../pages/Settings'));
const PrivacyPage = React.lazy(() => import('../../pages/Privacy'));
const TermsPage = React.lazy(() => import('../../pages/Terms'));
const CookiesPage = React.lazy(() => import('../../pages/Cookies'));
const WelcomePage = React.lazy(() => import('../../pages/Welcome'));
const ShareView = React.lazy(() => import('../../pages/ShareView'));
const InfoMaterialPage = React.lazy(() => import('../../pages/InfoMaterial'));
const YearReviewPage = React.lazy(() => import('../../pages/YearReview'));
const ChallengeResultsPage = React.lazy(() => import('../../pages/ChallengeResults'));
const LibraryPage = React.lazy(() => import('../../pages/Library'));

// Компонент загрузки
const LoadingSpinner = () => (
  <div className="min-h-screen flex items-center justify-center bg-gray-50">
    <div className="text-center">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
      <p className="mt-4 text-gray-600">Загрузка страницы...</p>
    </div>
  </div>
);

const BillingReturnRedirect = ({ failed = false }) => {
  const navigate = useNavigate();
  useEffect(() => {
    if (failed) {
      try {
        sessionStorage.setItem('noesa.billing.notice', JSON.stringify({
          type: 'error',
          title: 'Оплата не завершена',
          text: 'Платёж не был подтверждён. Можно попробовать ещё раз в разделе подписки.',
        }));
      } catch {}
    }
    navigate('/settings#billing', { replace: true });
  }, [failed, navigate]);
  return <LoadingSpinner />;
};

const LegacyWelcomeRedirect = () => {
  const location = useLocation();
  return (
    <Navigate
      to={{ pathname: location.pathname.startsWith('/en/') ? '/en' : '/', search: location.search, hash: location.hash }}
      replace
    />
  );
};

const CalendarLandingRedirect = () => {
  const location = useLocation();
  const target = publicSite.getPublicRedirect(`${location.pathname}${location.search}${location.hash}`);
  return <Navigate to={target || '/ai-planner'} replace />;
};

function PublicRouteLanguage() {
  const location = useLocation();
  const { language, setLanguage } = useI18n();
  React.useLayoutEffect(() => {
    const route = publicSite.resolvePublicRoute(location.pathname);
    if (route && route.language !== language) setLanguage(route.language);
    if (!route) clearPublicSeo();
  }, [location.pathname, language, setLanguage]);
  return null;
}

// Компонент оболочки приложения
const AppShell = React.lazy(() => import('../AppShell'));

const AppRouter = () => {
  const RouteTracker = () => {
    const location = useLocation();
    const prevRef = React.useRef('');
    useEffect(() => {
      try {
        const path = location.pathname + location.search;
        const title = typeof document !== 'undefined' ? document.title : undefined;
        const referer = prevRef.current || undefined;
        const authState = (() => {
          try {
            const raw = localStorage.getItem('ai_diary_auth');
            const u = raw ? (JSON.parse(raw)?.user || null) : null;
            return (u && (u.id || u.user_id || u.username)) ? 'user' : 'guest';
          } catch { return 'guest'; }
        })();
        const publicRoute = publicSite.resolvePublicRoute(location.pathname);
        const basePath = publicRoute ? publicSite.publicPath(location.pathname, 'ru') : location.pathname;
        const first = (basePath || '/').split('/').filter(Boolean)[0] || 'root';
        const pageType = first === '' ? 'root' : first; // welcome, login, diary, chat, calendar, analytics, settings, privacy, terms, cookies, s
        const scr = (typeof window !== 'undefined' && window.screen) ? `${window.screen.width}x${window.screen.height}` : undefined;
        trackPageView(path, { title, referer, params: { page_type: pageType, auth_state: authState, screen: scr } });

        // Route-based funnel/activation goals (deduped in analytics layer)
        try {
          if (
            basePath === '/'
            || basePath === '/pricing'
            || basePath === '/ai-planner'
          ) trackGoal(METRIKA_GOALS.view_welcome);
          if (location.pathname.startsWith('/login')) trackGoal(METRIKA_GOALS.view_login);
          if (location.pathname.startsWith('/tracker')) trackGoal(METRIKA_GOALS.tracker_view);
          if (location.pathname.startsWith('/analytics')) trackGoal(METRIKA_GOALS.analytics_view);
          if (location.pathname.startsWith('/s/')) trackGoal(METRIKA_GOALS.share_link_opened);
        } catch {}

        prevRef.current = path;
      } catch (e) {
        // no-op
      }
    // Track only real "page" changes in SPA: pathname + search (ignore hash-only changes)
    }, [location.pathname, location.search]);
    return null;
  };

  return (
    <Router>
      <MeasurementTouchTracker />
      <Suspense fallback={<LoadingSpinner />}>
        <PublicRouteLanguage />
        <RouteTracker />
        <Routes>
          {/* Публичные маршруты */}
          <Route path="/" element={<WelcomePage />} />
          <Route path="/welcome" element={<LegacyWelcomeRedirect />} />
          <Route path="/ai-planner" element={<WelcomePage />} />
          <Route path="/ai-calendar" element={<CalendarLandingRedirect />} />
          <Route path="/ai-assistant" element={<WelcomePage />} />
          <Route path="/ai-photo-editor" element={<WelcomePage />} />
          <Route path="/ai-diary" element={<WelcomePage />} />
          <Route path="/ai-for-work" element={<WelcomePage />} />
          <Route path="/ai-for-productivity" element={<WelcomePage />} />
          <Route path="/ai-for-students" element={<WelcomePage />} />
          <Route path="/pricing" element={<WelcomePage />} />
          <Route path="/code" element={<WelcomePage />} />
          <Route path="/about" element={<WelcomePage />} />
          <Route path="/mobile" element={<WelcomePage />} />
          <Route path="/en" element={<WelcomePage />} />
          <Route path="/en/welcome" element={<LegacyWelcomeRedirect />} />
          <Route path="/en/ai-planner" element={<WelcomePage />} />
          <Route path="/en/ai-calendar" element={<CalendarLandingRedirect />} />
          <Route path="/en/ai-assistant" element={<WelcomePage />} />
          <Route path="/en/ai-photo-editor" element={<WelcomePage />} />
          <Route path="/en/ai-diary" element={<WelcomePage />} />
          <Route path="/en/ai-for-work" element={<WelcomePage />} />
          <Route path="/en/ai-for-productivity" element={<WelcomePage />} />
          <Route path="/en/ai-for-students" element={<WelcomePage />} />
          <Route path="/en/pricing" element={<WelcomePage />} />
          <Route path="/en/code" element={<WelcomePage />} />
          <Route path="/en/about" element={<WelcomePage />} />
          <Route path="/en/mobile" element={<WelcomePage />} />
          <Route path="/news" element={<InfoMaterialPage />} />
          <Route path="/news/:slug" element={<InfoMaterialPage />} />
          <Route path="/en/news" element={<InfoMaterialPage />} />
          <Route path="/en/news/:slug" element={<InfoMaterialPage />} />
          <Route path="/s/:token" element={<ShareView />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/privacy" element={<PrivacyPage />} />
          <Route path="/terms" element={<TermsPage />} />
          <Route path="/cookies" element={<CookiesPage />} />
          <Route path="/billing/success" element={<BillingReturnRedirect />} />
          <Route path="/billing/fail" element={<BillingReturnRedirect failed />} />
          
          {/* Защищенные маршруты */}
          <Route
            element={
              <ProtectedRoute>
                <AppShell />
              </ProtectedRoute>
            }
          >
            <Route path="/diary" element={<DiaryPage />} />
            <Route path="/chat" element={<ChatPage />} />
            <Route path="/library" element={<LibraryPage />} />
            <Route path="/calendar" element={<CalendarPage />} />
            <Route path="/tracker" element={<TrackerPage />} />
            <Route path="/analytics" element={<AnalyticsPage />} />
            <Route path="/brain" element={<BrainPage />} />
            <Route path="/year-review/:year" element={<YearReviewPage />} />
            <Route path="/challenge-results" element={<ChallengeResultsPage />} />
            <Route path="/settings" element={<SettingsPage />} />
          </Route>
          
          {/* Fallback для несуществующих маршрутов */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Suspense>
    </Router>
  );
};

export { AppRouter };
